import React, { use, useEffect, useState } from 'react';
import { Grid, Box, Typography, Button, IconButton } from '@mui/material';
import { LocationOnOutlined, ContactPhoneOutlined } from '@mui/icons-material';
import { ReactComponent as SaveContactIcon } from '../assests/SaveContact.svg';
import { saveToContacts } from '../utils/utils';
import apiClient from '../api/apiClient';
import OneTap from "../assests/White.png"
import SocialIcons from '../components/carousel/SocialIcons';
import LinkedInIcon from '@mui/icons-material/LinkedIn'; 
import { useParams } from 'react-router-dom';

// Base URL for API - use environment variable or fallback to localhost
const BASE_URL = process.env.REACT_APP_BASE_URL?.replace(/\/api\/?$/, '') || 'https://api.one-tap.io';

// API service
const api = {
  getProfile: async (username) => {
    try {
      return await apiClient.get(`profile/${username}/`).json();
    } catch (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
  }
};

// Helper function to get initials
const getInitials = (name) => {
  if (!name) return 'U';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

// Profile Avatar Component
const ProfileAvatar = ({ profilePicture, fullName, size = 88 }) => {
  const [imageError, setImageError] = useState(false);
  
  const initials = getInitials(fullName);
  const imageUrl = profilePicture ? `${BASE_URL}${profilePicture}` : null;

  if (imageUrl && !imageError) {
    return (
      <Box
        component="img"
        src={imageUrl}
        alt={fullName}
        onError={() => setImageError(true)}
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          objectFit: 'cover',
          border: '2px solid rgba(255,255,255,0.1)',
          mr: size === 88 ? 2 : 0
        }}
      />
    );
  }
  
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px solid rgba(255,255,255,0.1)',
        mr: size === 88 ? 2 : 0
      }}
    >
      <Typography 
        sx={{ 
          color: '#fff', 
          fontWeight: 600, 
          fontSize: size === 96 ? '32px' : '28px' 
        }}
      >
        {initials}
      </Typography>
    </Box>
  );
};

// Style objects
const styles = {
  container: { width: '100%', minHeight: '100vh', background: '#000', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start'},
  desktopCard1: { width: '100%', maxWidth: 750, background: '#000', backdropFilter: 'blur(10px)', borderRadius: 4, overflow: 'hidden', padding: 2, boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)', animation: 'fadeIn 0.5s ease-out', '@keyframes fadeIn': { '0%': { opacity: 0 }, '100%': { opacity: 1 } } },
  desktopCard2: { width: '100%', maxWidth: 750, background: 'rgba(18, 18, 18, 0.8)', backdropFilter: 'blur(10px)', borderRadius: 4, overflow: 'hidden', padding: 4, boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)', animation: 'fadeIn 0.5s ease-out', '@keyframes fadeIn': { '0%': { opacity: 0 }, '100%': { opacity: 1 } } },
  profileBox: { display: 'flex', alignItems: 'center', mb: 3, p:0 },
  avatar: (size = 100) => ({ width: size, height: size, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255, 255, 255, 0.1)', mx: "auto", my: "auto" }),
  nameText: { color: '#fff', fontWeight: 600, fontSize: '24px', mb: 0.5 },
  positionText: { color: '#fff', fontSize: '16px', mb: 0.5 },
  companyText: { color: '#9ca3af', fontSize: '14px', display: 'flex', alignItems: 'center', mt: 0.6 },
  dot: { display: 'inline-block', width: '4px', height: '4px', borderRadius: '50%', backgroundColor: 'rgba(255, 255, 255, 0.5)', mx: 1 },
  sectionCard: { background: '#1e1e1e', p: "20px", mb: "20px", backdropFilter: 'blur(10px)', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)', animation: 'fadeIn 0.5s ease-out', '@keyframes fadeIn': { '0%': { opacity: 0 }, '100%': { opacity: 1 } } },
  sectionTitle: { color: '#fff', fontWeight: "semibold", mb: 1, fontSize: '18px' },
  bioText: { color: '#d1d5db', fontSize: '14px', marginTop: 2 },
  socialIconsBox: { display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 },
  actionButtons: { display: 'flex', gap: 2, mt: 3 },
  saveButton: { textTransform: 'none', backgroundColor: '#fff', color: '#000', fontWeight: 'semibold', borderRadius: "8px", p: "10px", '&:hover': { backgroundColor: '#fff' } },
  connectButton: { textTransform: 'none', borderColor: 'rgba(255, 255, 255, 0.3)', color: '#fff', fontWeight: 500, borderRadius: 2, py: 1, px: 2, '&:hover': { borderColor: 'rgba(255, 255, 255, 0.5)' } },
  mobileView: { position: 'fixed', paddingBottom: "20px", bottom: 0, left: 0, right: 0, background: 'rgba(18, 18, 18, 0.9)', backdropFilter: 'blur(10px)', borderTopLeftRadius: 16, borderTopRightRadius: 16, display: { xs: 'block', md: 'none' }, boxShadow: '0 -4px 30px rgba(0, 0, 0, 0.2)', zIndex: 1000, animation: 'slideUp 0.5s ease-out', '@keyframes slideUp': { '0%': { transform: 'translateY(100%)', opacity: 0 }, '100%': { transform: 'translateY(0)', opacity: 1 } }, border: '1px solid rgba(255, 255, 255, 0.08)', overflow: 'hidden' },
  socialIconsRow: { display: 'flex', justifyContent: 'space-around', padding: '16px 24px 14px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' },
  divider: { width: '40px', height: '4px', borderRadius: '2px', background: 'rgba(255, 255, 255, 0.2)', margin: '8px auto' },
  mobileProfileBox: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '4px 24px 24px' },
  mobileNameText: { color: '#fff', fontWeight: 600, fontSize: '20px', mb: 0.5, textAlign: 'center' },
  mobilePositionText: { color: '#d1d5db', fontSize: '16px', mb: 3, textAlign: 'center' },
  mobileButtonsBox: { display: 'flex', gap: 2, width: '100%' },
  brandLogo: { position: 'absolute', top: 16, right: 16, width: 32, height: 32 },
  logoImage: { width: '100%', height: '100%', borderRadius: 1 }
};

// Social media icon component
const SocialIcon = ({ platform, url, color, Icon, mobileStyle = false }) => {
  if (!url) return null;
  
  const baseStyle = mobileStyle ?
  { width: 78, height: 78, borderRadius: 2, padding: 0 } :
  { width: 78, height: 78, borderRadius: 2.5, '&:hover': { background: color } };

  const iconStyle = mobileStyle ? { color: '#fff', fontSize: 60 } : { color: '#fff', fontSize: 60 };

  return (
    <IconButton
      sx={{ background: color, ...baseStyle, mr:2 }}
      href={url}
      target="_blank"
    >
      <Icon sx={iconStyle} />
    </IconButton>
  );
};

// Profile Card Component
const ProfileCard = () => {
  const { username } = useParams();
  const [profileData, setProfileData] = useState(null);
  const [showMobileView, setShowMobileView] = useState(false);
  const [fullName, setFullName] = useState('');
  const [personalDetails, setPersonalDetails] = useState({});
  const [socialLinks, setSocialLinks] = useState({});
  const [contactLinks, setContactLinks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await api.getProfile(username);
        if (data) {
          setProfileData(data);
          setContactLinks({
            ...data.social_links,
            phone: data?.phone_number,
            email: data?.email_id
          });
          setTimeout(() => setShowMobileView(true), 500);
        } else {
          setError(true);
        }
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [username]);

  useEffect(() => {
    if (profileData) {
      setFullName(profileData.full_name);
      setPersonalDetails(profileData.personal_details);
      setSocialLinks(profileData.social_links);
    }
  }, [profileData]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const mobileViewElement = document.getElementById('mobile-profile-view');
      if (showMobileView && mobileViewElement && !mobileViewElement.contains(event.target)) {
        setShowMobileView(false);
      }
    };

    const handleScroll = () => showMobileView && setShowMobileView(false);

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [showMobileView]);

  // Contact info for vCard
  const contactInfo = {
    name: fullName,
    position: personalDetails?.position,
    company: personalDetails?.current_company,
    email: profileData?.email_id,
    phone: profileData?.phone_number,
    location: {
      city: personalDetails?.location_city,
      country: personalDetails?.location_country
    },
    socialMedia: contactLinks
  };


  if (!username) {
    return (
      <Box sx={styles.container}>
        <Typography color="error" mt={8} fontSize={22} fontWeight={600}>
          No username provided.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={styles.container}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '50vh' 
        }}>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            border: '3px solid rgba(255, 255, 255, 0.2)',
            borderTop: '3px solid #fff',
            animation: 'spin 1s linear infinite',
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' }
            }
          }} />
          <Typography color="#fff" mt={2} fontSize={18}>
            Loading profile...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={styles.container}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '50vh',
          textAlign: 'center'
        }}>
          <Typography color="#fff" fontSize={72} fontWeight={600} mb={2}>
            404
          </Typography>
          <Typography color="#9ca3af" fontSize={20} mb={1}>
            Profile Not Found
          </Typography>
          <Typography color="#9ca3af" fontSize={16}>
            The profile you're looking for doesn't exist or has been removed.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      {/* Desktop Profile Card */}
      <Grid container sx={styles.desktopCard1} spacing={3} md={8} mx="auto" xs={12} >
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" mt={4}>
          <Typography textAlign="left" fontWeight={600}>
            You have connected with
          </Typography>
          <img src={OneTap} width={32} height={32} alt="OneTap Icon" />
        </Box>

        {/* Profile Info Section */}
        <Grid sx={styles.profileBox} item xs={12} p={0} m={0}>
          <Grid item xs={4} textAlign="center" justifyContent="center" alignItems="center">
            <ProfileAvatar 
              profilePicture={profileData?.profile_picture}
              fullName={fullName}
              size={88}
            />
          </Grid>
          <Grid item xs={7}>
            <Box sx={{ ml: 2}}>
              <Typography variant="h5" sx={styles.nameText}>{fullName}</Typography>
              <Typography variant="body1" sx={styles.positionText}>{personalDetails?.position}</Typography>
              <Typography variant="body2" sx={styles.companyText}>
                {personalDetails?.current_company}
              </Typography>
              <Typography variant="body2" sx={styles.companyText}>
                {personalDetails?.location_city && (
                  <>
                    <LocationOnOutlined sx={{ fontSize: 18, color: 'rgba(255, 255, 255, 0.5)', mr: 0.5 }} />
                    {personalDetails.location_city}, {personalDetails.location_country}
                  </>
                )}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Bio Section */}
        <Grid sx={styles.sectionCard} item xs={12}>
          <Typography variant="h6" sx={styles.sectionTitle}>Bio</Typography>
          <Typography variant="body1" sx={styles.bioText}>{personalDetails?.bio}</Typography>
        </Grid>

        {/* Contact Section */}
        <SocialIcons social_links={contactLinks} />

        <Grid container fullWidth mx="auto" xs={12} mt={2} textAlign="center">
          <Grid item xs={5} sx={{ padding: '0 !important' }}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => saveToContacts(contactInfo)}
              startIcon={<SaveContactIcon height={24} width={24} />}
              sx={styles.saveButton}
            >
              Save Contact
            </Button>
          </Grid>
          <Grid item xs={7} sx={{ padding: '0 0 0 16px !important' }}>
            {socialLinks?.linkedin && (
              <Button 
                fullWidth
                variant="contained"
                href={socialLinks.linkedin}
                target="_blank"
                startIcon={<LinkedInIcon height={28} width={28} />}
                sx={styles.saveButton}
              >
                Connect on LinkedIn
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>

      {/* Mobile View (Slide Up Animation) */}
      {showMobileView && (
        <Box
          id="mobile-profile-view"
          sx={{
            ...styles.mobileView,
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            // minHeight: "400px",
            zIndex: 1000,
            display: { xs: 'block', md: 'none' }
          }}
        >
          {/* Horizontal Divider */}
          <Box sx={styles.divider} />
          
          {/* Profile Info */}
          <Box sx={styles.mobileProfileBox}>
            <ProfileAvatar 
              profilePicture={profileData?.profile_picture}
              fullName={fullName}
              size={96}
            />
            
            <Typography variant="h6" mt={2} sx={styles.mobileNameText}>{fullName}</Typography>
            <Typography variant="body1" sx={styles.mobilePositionText}>{personalDetails?.position}</Typography>
            
            <Grid container fullWidth mx="auto" xs={12} mt={2} textAlign="center">
              <Grid item xs={5} sx={{ padding: '0 !important' }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => saveToContacts(contactInfo)}
                  startIcon={<SaveContactIcon height={24} width={24} />}
                  sx={styles.saveButton}
                >
                  Save Contact
                </Button>
              </Grid>
              <Grid item xs={7} sx={{ padding: '0 0 0 16px !important' }}>
                {socialLinks?.linkedin && (
                  <Button 
                    fullWidth
                    variant="contained"
                    href={socialLinks.linkedin}
                    target="_blank"
                    startIcon={<LinkedInIcon height={28} width={28} />}
                    sx={styles.saveButton}
                  >
                    Connect on LinkedIn
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ProfileCard;